import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { channelManager } from "../realtime/ChannelManager";
import { getActivityFeed } from "./api";

type Options = {
  // "always" forces a refetch on the cold first mount of the shell even when
  // staleTime: Infinity would otherwise serve cached data. On the /community
  // round-trip the rail never unmounts; freshness there is restored by enabled
  // flipping false->true (see below), not by this option.
  refetchOnMount?: boolean | "always";
  // false skips both the realtime subscription and the community_activity_feed
  // RPC call. The rail passes enabled=false on /community so it doesn't fetch
  // or subscribe for a feed that won't render — the /community route owns the
  // single community:feed subscription while it's active. Leaving /community
  // flips enabled false->true, which re-runs the query.
  enabled?: boolean;
};

export function useCommunityFeed(limit = 30, options: Options = {}) {
  const enabled = options.enabled ?? true;
  // With staleTime: Infinity and data already cached, React Query silently
  // swallows a failed background refetch (realtime invalidation / reconnect):
  // status stays "success", isError/failureCount never flip. Intercept at the
  // queryFn boundary — the one place every fetch outcome is visible — so the
  // rail can warn that it's showing stale data instead of pretending it's live.
  const [refreshFailed, setRefreshFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    return channelManager.subscribeToCommunityFeed();
  }, [enabled]);

  const query = useQuery({
    queryKey: ["community", "feed", limit],
    queryFn: async () => {
      try {
        const data = await getActivityFeed(limit);
        setRefreshFailed(false);
        return data;
      } catch (err) {
        setRefreshFailed(true);
        throw err;
      }
    },
    staleTime: Infinity,
    refetchOnMount: options.refetchOnMount,
    enabled,
  });

  // Return only the consumed fields rather than spreading the result — spreading
  // reads every tracked property and defeats React Query's re-render optimization,
  // re-rendering the rail on every background refetch even when data is unchanged.
  return { data: query.data, isLoading: query.isLoading, isError: query.isError, refreshFailed };
}
